import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/Field';
import { Proposals } from './Proposals';
import { useAsk, type AskTurn } from './queries';

/**
 * Ask a question about this wedding.
 *
 * Read-only by construction: the server's tools look things up and none of them
 * writes, so the worst a wrong answer can do is be wrong.
 *
 * Rendered inside `AskLauncher`'s sheet, which supplies the close control — this
 * component is only the conversation.
 *
 * History is kept in component state and capped. It is deliberately **not**
 * persisted: a tool loop re-sends the whole conversation on every round, so a
 * long remembered history is a bill that grows for an answer nobody reads
 * differently, and nobody has asked to re-read last week's questions.
 */

/** Pairs of turns kept. The server caps it too; this stops sending what it would drop. */
const MAX_TURNS = 10;

export function AskPanel({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const ask = useAsk(wedId);
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [question, setQuestion] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [turns.length, ask.isPending]);

  const lastProposals = turns[turns.length - 1]?.proposals ?? [];

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || ask.isPending) return;

    const history = turns.slice(-MAX_TURNS);
    setTurns((prev) => [...prev, { role: 'user', text: q }]);
    setQuestion('');

    ask.mutate(
      { question: q, history },
      {
        onSuccess: (reply) =>
          setTurns((prev) => [
            ...prev,
            { role: 'assistant', text: reply.text, proposals: reply.proposals },
          ]),
        // The question stays on screen with the failure under it, rather than
        // vanishing as though it was never asked.
        onError: (err) => setTurns((prev) => [...prev, { role: 'assistant', text: err.message }]),
      },
    );
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-section">{t('ask.title')}</h2>
        {turns.length > 0 && (
          <button
            type="button"
            onClick={() => setTurns([])}
            className="min-h-tap text-caption text-leaf-soft underline underline-offset-4"
          >
            {t('ask.clear')}
          </button>
        )}
      </div>

      {turns.length === 0 ? (
        <p className="max-w-prose text-caption text-leaf-soft">{t('ask.hint')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {turns.map((turn, i) => (
            <li
              key={`${i}-${turn.text.slice(0, 12)}`}
              className={turn.role === 'user' ? 'flex justify-end' : 'flex'}
            >
              <span
                className={`max-w-[42rem] whitespace-pre-line rounded px-3 py-2 text-body ${
                  turn.role === 'user'
                    ? 'bg-cream text-ink'
                    : 'border border-line bg-paper text-ink'
                }`}
              >
                {turn.text}
              </span>
            </li>
          ))}
          {/* Under the last answer only: an older turn's proposals have been
              dealt with or deliberately passed over, and re-offering them is how
              somebody confirms the one they already declined. */}
          {lastProposals.length > 0 && (
            <li>
              <Proposals wedId={wedId} proposals={lastProposals} />
            </li>
          )}
          {ask.isPending && (
            <li className="flex">
              <span className="rounded border border-line px-3 py-2 text-body text-leaf-soft">
                {t('ask.thinking')}
              </span>
            </li>
          )}
        </ul>
      )}
      <div ref={endRef} />

      <form className="flex flex-wrap items-center gap-2" onSubmit={onSubmit}>
        <label className="min-w-[14rem] flex-1">
          <span className="sr-only">{t('ask.title')}</span>
          <TextInput
            placeholder={t('ask.placeholder')}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>
        <Button type="submit" variant="secondary" loading={ask.isPending}>
          {t('ask.send')}
        </Button>
      </form>
      <p className="text-caption text-leaf-soft">{t('ask.readOnly')}</p>
    </section>
  );
}
